import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ISeriesApi } from 'lightweight-charts';
import { useTheme } from '../contexts/ThemeContext';
import { ChartPosition, useChartPositionContext } from '../contexts/ChartPositionContext';
import { usePositionContext } from '../contexts/PositionContext';
import { getAssetDecimalPlaces } from '../utils/priceFormatter';
import { useIsMobile } from '../hooks/useIsMobile';

type LevelKind = 'entry' | 'tp' | 'sl';

interface ChartLevelOverlayProps {
  position: ChartPosition;
  /**
   * The actual lightweight-charts series. Every price/pixel conversion goes through it rather
   * than being recalculated by hand.
   */
  series: ISeriesApi<any>;
  /** Live market price, used to decide whether a resting order is a limit or a stop. */
  currentPrice: number;
  overlayRef: React.RefObject<HTMLDivElement>;
}

interface DragState {
  kind: LevelKind;
  price: number;
  moved: boolean;
}

/**
 * Something the user has asked for that has not happened yet, waiting on Confirm or Discard.
 *
 * Every destructive or position-altering action on the overlay goes through this, so they all
 * ask the same way. Removing a level or closing a position used to happen on the single click
 * of an `x` sitting a few pixels from the drag handle, while merely *moving* a level asked
 * first — the cheap action was guarded and the expensive ones were not.
 */
type PendingAction =
  | { type: 'move'; kind: 'tp' | 'sl'; price: number }
  | { type: 'remove'; kind: 'tp' | 'sl' }
  | { type: 'close'; kind: 'entry' };

/** How far the controls sit in from the left edge of the chart. */
const LEFT_INSET = 6;

/** Used until the price scale reports its real width, and if it ever refuses to. */
const FALLBACK_AXIS_WIDTH = 64;

/**
 * Half the height of the tallest row, so a level pinned to the top or bottom of the plot has
 * its whole chip inside rather than half of it over the toolbar or the time axis.
 */
const ROW_HALF_HEIGHT = 14;

/**
 * Height of the chart's time axis. The library gives no way to ask for it from a series, and
 * being a few pixels out here only shifts a pinned level slightly — it is a backstop for a
 * level that is off-screen anyway, not a placement anything is read from.
 */
const TIME_AXIS_HEIGHT = 28;

/**
 * Hairline weight for every level's line. The entry still reads as the heavier of the three
 * because it is solid where the exits are dashed, so it needs no extra weight of its own.
 *
 * Applied as a full pixel of layout scaled down rather than as a fractional `height`. A box
 * half a pixel tall is laid out fine but has nowhere to rasterise a background *image* into,
 * and the dashed lines are a repeating gradient — so the targets and stops were being painted
 * as nothing at all while the solid entry, a plain background colour, survived. Scaling a
 * whole pixel gives the gradient a row to render in and the compositor thins it afterwards.
 */
const LINE_THICKNESS = 0.5;

const TP_COLOR = '#2AB7A6';
const SL_COLOR = '#ea580c';

/**
 * On-chart entry / take-profit / stop-loss controls for one position or resting order.
 *
 * Placement goes through `series.priceToCoordinate()` and drags resolve with
 * `series.coordinateToPrice()`. The previous implementation derived its own pixel mapping from
 * `priceScale.getVisibleRange()` and the overlay's full height, which ignored the price scale's
 * 10% margins, the time axis along the bottom and the price axis down the right, then added
 * fixed +36px/+40px offsets on top. The controls were drawn away from the price they described
 * and the error changed with the zoom level. The series' own conversions account for all of it.
 */
const ChartLevelOverlay: React.FC<ChartLevelOverlayProps> = ({
  position,
  series,
  currentPrice,
  overlayRef
}) => {
  const { theme } = useTheme();
  const { setSLLevel, setTPLevel, closeChartPosition } = useChartPositionContext();
  const { editOrder, closingPositions } = usePositionContext();
  const isMobile = useIsMobile();

  const [drag, setDrag] = useState<DragState | null>(null);
  const dragRef = useRef<DragState | null>(null);
  // Width of the price axis, so the tag can sit exactly over it and the line can stop exactly
  // where it begins. Measured rather than assumed: the axis is as wide as the longest price it
  // has to print, so it differs between a five-figure and a sub-dollar asset.
  const [axisWidth, setAxisWidth] = useState(FALLBACK_AXIS_WIDTH);
  const axisWidthRef = useRef(axisWidth);
  // Whatever the user has asked for and not yet confirmed. One at a time: asking for a second
  // thing replaces the first, so two rows can never both be waiting on an answer.
  const [pending, setPending] = useState<PendingAction | null>(null);
  const pendingRef = useRef<PendingAction | null>(null);
  const rowRefs = useRef<Partial<Record<LevelKind, HTMLDivElement | null>>>({});
  // Detaches the window-level drag listeners, so a symbol change mid-gesture does not leave
  // them bound to a component that is gone.
  const endDragRef = useRef<(() => void) | null>(null);

  useEffect(() => () => endDragRef.current?.(), []);

  const entryPrice = parseFloat(position.entry);
  const isBuy = position.type === 'Buy';
  const isPending = position.isPendingOrder === true;
  const isDark = theme === 'dark';
  const isClosing = closingPositions.has(position.id);

  // Passing the price matters: called with only a symbol this falls back to coarse pattern
  // matching, which would round a level's price on its way back into the order.
  const decimals = useMemo(
    () => getAssetDecimalPlaces(position.symbol, entryPrice),
    [position.symbol, entryPrice]
  );

  const formatPrice = useCallback((price: number) => price.toFixed(decimals), [decimals]);

  /**
   * Which side of the market an order rests on decides its kind, and the fill checks in
   * PositionContext agree: a buy limit triggers at or below its price, a buy stop at or above.
   */
  const orderTypeForPrice = useCallback(
    (price: number): 'limit' | 'stop' => {
      if (!Number.isFinite(currentPrice) || currentPrice <= 0) {
        return position.orderType === 'stop' ? 'stop' : 'limit';
      }
      if (isBuy) return price <= currentPrice ? 'limit' : 'stop';
      return price >= currentPrice ? 'limit' : 'stop';
    },
    [currentPrice, isBuy, position.orderType]
  );

  // A stop must stay on the losing side of the entry and a target on the winning side. An
  // unfilled order's entry has no such limit, which is the point of moving it on the chart.
  const clampToValidRange = useCallback(
    (kind: LevelKind, price: number): number => {
      if (price <= 0) return 0;
      if (kind === 'entry') return price;

      const tick = Math.pow(10, -decimals);
      const wantsAbove = kind === 'tp' ? isBuy : !isBuy;

      return wantsAbove ? Math.max(price, entryPrice + tick) : Math.min(price, entryPrice - tick);
    },
    [decimals, entryPrice, isBuy]
  );

  const tpIsSet = position.tpLevel !== undefined;
  const slIsSet = position.slLevel !== undefined;

  // A level that has not been set yet has no price of its own, so its row is parked at the
  // entry and only its chip shows. Dragging that chip is what brings it into existence.
  const rows = useMemo(
    () => [
      { kind: 'entry' as LevelKind, price: entryPrice, isSet: true },
      { kind: 'tp' as LevelKind, price: position.tpLevel ?? entryPrice, isSet: tpIsSet },
      { kind: 'sl' as LevelKind, price: position.slLevel ?? entryPrice, isSet: slIsSet }
    ],
    [entryPrice, position.tpLevel, position.slLevel, tpIsSet, slIsSet]
  );

  // Keep every row glued to its price.
  //
  // Driven by an animation frame rather than a chart event because the price scale is on
  // autoScale: its visible range shifts as candles arrive and lightweight-charts emits no event
  // for that. Positions are written straight to the DOM, so following the chart costs no
  // re-renders.
  useEffect(() => {
    let frame: number;

    const sync = () => {
      frame = requestAnimationFrame(sync);
      const active = dragRef.current;

      // Read on the same loop as the positions, because the axis grows and shrinks with the
      // prices on it. A render only happens when it actually changes, which is rare.
      let measured = FALLBACK_AXIS_WIDTH;
      try {
        measured = series.priceScale().width() || FALLBACK_AXIS_WIDTH;
      } catch {
        // The series can be disposed mid-frame, e.g. on a symbol change.
      }
      if (measured !== axisWidthRef.current) {
        axisWidthRef.current = measured;
        setAxisWidth(measured);
      }

      // Bottom of the drawable area, in the same pixel space the level coordinates are in.
      // The overlay covers the whole chart, so its own height less the time axis is the pane.
      //
      // Deliberately not derived from `priceScale().getVisibleRange()`: converting that range
      // back to coordinates lands on the scale's 10% margins rather than the edges of the
      // pane, and clamping to those would shove a level that is perfectly visible near the
      // top of the chart down to the 10% line.
      const overlayHeight = overlayRef.current?.clientHeight ?? 0;
      const plotBottom = overlayHeight > 0 ? overlayHeight - TIME_AXIS_HEIGHT : Infinity;

      rows.forEach(({ kind, price }) => {
        const row = rowRefs.current[kind];
        if (!row) return;

        const dragging = active?.kind === kind;
        const held = pendingRef.current;
        // A level being dragged follows the pointer; one dropped and awaiting confirmation
        // stays where it was dropped rather than snapping back to its stored price. A level
        // waiting on a removal has not moved, so it stays at the price it already has.
        const dropped = held?.type === 'move' && held.kind === kind ? held.price : null;
        const effectivePrice = dragging ? active!.price : dropped ?? price;
        const y = series.priceToCoordinate(effectivePrice);

        if (y === null) {
          // Outside the visible range, so there is no coordinate to sit at.
          row.style.visibility = 'hidden';
          return;
        }

        // A stop or target well away from the current price converts to a coordinate above
        // or below the plot, and the row was drawn there — over the timeframe toolbar, or
        // under the time axis. Pinning it to the edge instead keeps it reachable, so a stop
        // the market has run away from can still be moved or removed, and says plainly that
        // the level is off-screen in that direction.
        const pinned = Math.min(
          Math.max(y, ROW_HALF_HEIGHT),
          Math.max(ROW_HALF_HEIGHT, plotBottom - ROW_HALF_HEIGHT)
        );

        row.style.visibility = 'visible';
        row.style.transform = `translateY(${pinned}px)`;
      });
    };

    frame = requestAnimationFrame(sync);
    return () => cancelAnimationFrame(frame);
  }, [rows, series]);

  /** Put an action in front of the user rather than performing it. */
  const askFor = useCallback((action: PendingAction) => {
    pendingRef.current = action;
    setPending(action);
  }, []);

  const commit = useCallback(
    (kind: LevelKind, price: number) => {
      if (!Number.isFinite(price) || price <= 0) return;

      if (kind === 'tp') {
        setTPLevel(position.id, price);
        return;
      }

      if (kind === 'sl') {
        setSLLevel(position.id, price);
        return;
      }

      // Moving a resting order has to be written back to the order itself. The previous
      // implementation only updated the chart's own copy, so the next sync with the order book
      // pulled it straight back to where it started.
      if (isPending) {
        editOrder(position.id, formatPrice(price), position.size, orderTypeForPrice(price));
      }
    },
    [editOrder, formatPrice, isPending, orderTypeForPrice, position.id, position.size, setSLLevel, setTPLevel]
  );

  const beginDrag = useCallback(
    (event: React.PointerEvent, kind: LevelKind, startPrice: number) => {
      // A filled entry cannot move: the fill already happened at a price.
      if (kind === 'entry' && !isPending) return;
      // An AI trade's entry is placed by the strategy; only its exits can be moved by hand.
      if (kind === 'entry' && position.isAI) return;

      event.preventDefault();
      // Without this the chart underneath treats the gesture as a pan.
      event.stopPropagation();

      const initial: DragState = { kind, price: startPrice, moved: false };
      dragRef.current = initial;
      setDrag(initial);

      const priceAtPointer = (clientY: number): number | null => {
        const bounds = overlayRef.current?.getBoundingClientRect();
        if (!bounds) return null;

        const raw = series.coordinateToPrice(clientY - bounds.top);
        if (raw === null) return null;

        return clampToValidRange(kind, Number(raw));
      };

      const handleMove = (moveEvent: PointerEvent) => {
        const price = priceAtPointer(moveEvent.clientY);
        if (price === null || price <= 0) return;

        const next: DragState = { kind, price, moved: true };
        dragRef.current = next;
        setDrag(next);
      };

      const detach = () => {
        window.removeEventListener('pointermove', handleMove);
        window.removeEventListener('pointerup', handleUp);
        window.removeEventListener('pointercancel', handleUp);
        endDragRef.current = null;
      };

      const handleUp = (upEvent: PointerEvent) => {
        detach();

        const state = dragRef.current;
        const price = priceAtPointer(upEvent.clientY) ?? state?.price ?? null;

        dragRef.current = null;
        setDrag(null);

        // A click that never moved should not create or shift a level.
        if (price === null || !state?.moved) return;

        if (kind === 'entry') {
          // A resting order moves as soon as it is dropped, the way it does on TradingView.
          commit(kind, price);
          return;
        }

        // A target or stop is only applied once confirmed, so a mis-drag can be undone.
        askFor({ type: 'move', kind: kind as 'tp' | 'sl', price });
      };

      // On the window rather than the handle, and without pointer capture, because an unset
      // level's chip starts out in the entry row and moves to its own row the moment the drag
      // begins. Listeners bound to the handle would go with it and the gesture would die on
      // the first move.
      window.addEventListener('pointermove', handleMove);
      window.addEventListener('pointerup', handleUp);
      window.addEventListener('pointercancel', handleUp);
      endDragRef.current = detach;
    },
    [askFor, clampToValidRange, commit, isPending, overlayRef, position.isAI, series]
  );

  const clearLevel = useCallback(
    (kind: 'tp' | 'sl') => {
      if (kind === 'tp') setTPLevel(position.id, undefined);
      else setSLLevel(position.id, undefined);
    },
    [position.id, setSLLevel, setTPLevel]
  );

  const confirmPending = useCallback(() => {
    const action = pendingRef.current;
    pendingRef.current = null;
    setPending(null);
    if (!action) return;

    if (action.type === 'move') commit(action.kind, action.price);
    else if (action.type === 'remove') clearLevel(action.kind);
    else closeChartPosition(position.id);
  }, [clearLevel, closeChartPosition, commit, position.id]);

  const discardPending = useCallback(() => {
    pendingRef.current = null;
    setPending(null);
  }, []);

  /** What Confirm is about to do, for the button's tooltip. */
  const pendingDescription = (action: PendingAction): string => {
    if (action.type === 'move') return `Apply this ${action.kind.toUpperCase()}`;
    if (action.type === 'remove') {
      return position.isAI
        ? `Remove ${action.kind.toUpperCase()} and go back to the strategy's own level`
        : `Remove ${action.kind.toUpperCase()}`;
    }
    return isPending ? 'Cancel this order' : 'Close this position';
  };

  const closeIcon = isClosing ? (
    <div className="w-3 h-3 border border-gray-400 border-t-transparent rounded-full animate-spin" />
  ) : (
    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );

  /**
   * What a level is worth if the price reaches it, measured from the entry. A target reads as a
   * gain and a stop as a loss, so the box says how much is being risked or aimed for rather
   * than repeating the position's current P&L on all three rows.
   */
  const amountAtLevel = useCallback(
    (levelPrice: number): string => {
      const size = parseFloat(position.size);
      if (!Number.isFinite(size) || !Number.isFinite(entryPrice)) return '—';

      const delta = isBuy ? levelPrice - entryPrice : entryPrice - levelPrice;
      const value = delta * size;
      return `${value >= 0 ? '+' : '-'}$${Math.abs(value).toFixed(2)}`;
    },
    [entryPrice, isBuy, position.size]
  );

  const midBlockText = (kind: LevelKind, levelPrice: number) => {
    // While a resting order is dragged the label previews the kind it is about to become.
    if (kind === 'entry') return isPending ? orderTypeForPrice(levelPrice).toUpperCase() : position.pnl;
    return amountAtLevel(levelPrice);
  };

  /** The qty / PnL / close group, styled per level as it was before. */
  const detailBox = (kind: LevelKind, price: number) => {
    const accent = kind === 'tp' ? TP_COLOR : kind === 'sl' ? SL_COLOR : isBuy ? '#2AB7A6' : '#ef4444';
    const isEntry = kind === 'entry';

    return (
      <div
        className={`chart-position-container flex items-center justify-between rounded-sm transition-all duration-200 mx-1 border ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}
        style={{ minWidth: '60px', outline: 'none', boxShadow: 'none', borderColor: accent }}
      >
        {/* Quantity */}
        <div
          className="flex items-center py-1 text-xs font-normal rounded-l-sm"
          style={
            isEntry
              ? { minWidth: '20px', padding: '0px 6px', background: accent, color: '#fff' }
              : {
                  minWidth: '20px',
                  padding: '0px 6px',
                  color: accent,
                  borderRight: `1px solid ${accent}4d`
                }
          }
        >
          {parseFloat(position.size).toFixed(2)}
        </div>

        {/* Order type for a resting order, P&L once filled */}
        <div
          className="flex items-center justify-center px-1 py-0 text-xs font-medium"
          style={{
            minWidth: '55px',
            // The sign and the amount belong together: with a four-figure value the block used
            // to wrap and push the sign onto its own line. It grows instead.
            whiteSpace: 'nowrap',
            color: isEntry
              ? isPending
                ? accent
                : position.pnl.startsWith('-')
                  ? '#ef4444'
                  : '#22c55e'
              : accent
          }}
        >
          {midBlockText(kind, price)}
        </div>

        {/* Close the position / cancel the order, or clear just this level. Asks first —
            these sit a few pixels from the drag handle and none of them can be undone. */}
        <button
          onPointerDown={event => event.stopPropagation()}
          onClick={() =>
            askFor(isEntry ? { type: 'close', kind: 'entry' } : { type: 'remove', kind: kind as 'tp' | 'sl' })
          }
          disabled={isEntry && isClosing}
          title={
            isEntry
              ? isPending
                ? 'Cancel order'
                : 'Close position'
              : position.isAI
                ? `Remove ${kind.toUpperCase()} and go back to the strategy's own level`
                : `Remove ${kind.toUpperCase()}`
          }
          className={`flex items-center justify-center w-6 h-4 rounded-r-sm transition-colors duration-200 border-l ${
            isEntry && isClosing
              ? 'opacity-50 cursor-not-allowed'
              : isDark
                ? 'hover:bg-gray-700'
                : 'hover:bg-gray-100'
          }`}
          style={{ outline: 'none', boxShadow: 'none', borderLeftColor: `${accent}4d`, color: accent }}
        >
          {closeIcon}
        </button>
      </div>
    );
  };

  const originBadge = (
    <div
      className={`px-1 py-0.5 text-xs rounded mx-1 ${
        position.isAI ? 'bg-purple-500/20 text-purple-400' : 'bg-green-500/20 text-green-400'
      }`}
    >
      {position.isAI ? 'AI' : 'MI'}
    </div>
  );

  const levelChip = (kind: 'tp' | 'sl', price: number) => {
    const accent = kind === 'tp' ? TP_COLOR : SL_COLOR;
    return (
      <button
        key={kind}
        onPointerDown={event => beginDrag(event, kind, price)}
        title={
          position.isAI
            ? `Drag to override the strategy's ${kind.toUpperCase()}`
            : `Drag to set ${kind.toUpperCase()}`
        }
        className={`px-2 py-1 text-xs font-medium rounded transition-colors duration-200 mx-1 border ${
          kind === 'tp' ? 'border-dotted' : 'border-dashed'
        }`}
        style={{
          padding: '2px 4px',
          outline: 'none',
          boxShadow: 'none',
          color: accent,
          borderColor: `${accent}4d`,
          background: `${accent}1a`,
          cursor: 'ns-resize',
          touchAction: 'none'
        }}
      >
        {kind.toUpperCase()}
      </button>
    );
  };

  /**
   * Targets and stops that have no price yet. Their buttons sit in the entry's row, in the same
   * spot Confirm and Cancel appear in, so everything the position offers is on one line instead
   * of two loose chips floating above and below it. Dragging one out is what gives it a price
   * and moves it onto a row of its own.
   */
  const unplacedLevels = (['tp', 'sl'] as const).filter(kind => {
    const isSet = kind === 'tp' ? tpIsSet : slIsSet;
    return !isSet && drag?.kind !== kind && pending?.kind !== kind;
  });

  const renderRow = (kind: LevelKind, price: number, isSet: boolean) => {
    const isDragging = drag?.kind === kind;
    const awaiting = pending?.kind === kind ? pending : null;
    const isAwaiting = awaiting !== null;
    // Only a dropped level has a price of its own to preview. One waiting on a removal is
    // still sitting where it always was.
    const droppedPrice = awaiting?.type === 'move' ? awaiting.price : null;
    const shownPrice = isDragging ? drag!.price : droppedPrice ?? price;

    // An unplaced target or stop shows nothing here; its button is in the entry row. The row
    // itself stays mounted either way so the level can appear mid-drag without remounting.
    const showDetail = kind === 'entry' || isSet || isDragging || isAwaiting;
    // AI trades can have their exits dragged too: an untouched level defers to the strategy's
    // own thresholds, and one that is moved overrides them.
    const draggable = kind === 'entry' ? isPending && !position.isAI : true;

    const accent = kind === 'tp' ? TP_COLOR : kind === 'sl' ? SL_COLOR : isBuy ? '#2AB7A6' : '#ef4444';
    const dashed = kind !== 'entry';

    // Left to right: the controls sit at the left edge of the chart, the line takes up
    // whatever is left between them and the price axis, and the tag covers the axis itself.
    const lineRight = axisWidth;

    return (
      <div
        key={kind}
        ref={node => {
          rowRefs.current[kind] = node;
        }}
        className={`absolute ${kind === 'entry' ? 'position-executed-ordered-container' : kind === 'tp' ? 'takeprofit-container' : 'stoploss-container'}`}
        style={{
          // Spans the chart so its children can be measured off the right edge, which is
          // where the price axis is. Anchoring the row itself at a fixed distance from that
          // edge meant the line and the tag were laid out from a point that had nothing to
          // do with where the axis actually started, and both ran off the side of the chart
          // on any asset whose prices were wider than the guess.
          left: 0,
          right: 0,
          top: 0,
          // Zero-height, so the row's own line is exactly the price coordinate and each child
          // centres itself on it. Box height therefore cannot introduce an offset, which is
          // what the old fixed +36px/+40px adjustments were compensating for.
          height: 0,
          visibility: 'hidden',
          zIndex: isDragging ? 30 : 25
        }}
      >
        {/* Controls at the left edge, then the line running on to the price axis.
            
            Laid out as a flex row rather than as two absolutely positioned boxes so the line
            simply takes whatever space the controls leave. The controls' width depends on
            what the row is showing — a bare chip, or Discard and Confirm as well — so any
            fixed start for the line would be wrong half the time.
            
            The row has no height, so `align-items: center` puts both children's centres on
            the price coordinate exactly. */}
        <div
          className="absolute flex items-center"
          style={{
            left: `${LEFT_INSET}px`,
            right: `${lineRight}px`,
            top: 0,
            height: 0,
            zIndex: 55
          }}
        >
          <div
            onPointerDown={draggable ? event => beginDrag(event, kind, shownPrice) : undefined}
            className={`p-0 py-1 rounded-sm transition-all duration-200 pointer-events-auto flex-shrink-0 ${
              showDetail
                ? `border shadow-xs ${isDark ? 'bg-gray-800/50 border-gray-800' : 'bg-white border-gray-100'}`
                : ''
            }`}
            style={{
              cursor: draggable ? (isDragging ? 'grabbing' : 'ns-resize') : 'default',
              touchAction: 'none',
              transform: isMobile ? 'scale(0.75)' : undefined,
              // Scaling from the left keeps the controls anchored where they start, so the
              // mobile scale shortens the box rather than sliding it along the line.
              transformOrigin: 'left center'
            }}
            title={draggable ? 'Drag to move this level' : ''}
          >
            <div className="flex items-center">
              {/* Nothing the user asks for on this row happens until it is confirmed here —
                  a dropped level, a removed level, or a closed position alike. */}
              {awaiting && (
                <>
                  <button
                    onPointerDown={event => event.stopPropagation()}
                    onClick={discardPending}
                    title="Leave things as they are"
                    className={`text-xs font-medium rounded transition-colors duration-200 mx-1 border ${
                      isDark
                        ? 'bg-gray-500/20 text-gray-300 hover:bg-gray-500/30 border-gray-700'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 border-gray-200'
                    }`}
                    style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
                  >
                    Discard
                  </button>
                  <button
                    onPointerDown={event => event.stopPropagation()}
                    onClick={confirmPending}
                    title={pendingDescription(awaiting)}
                    className={`text-xs font-medium rounded transition-colors duration-200 mx-1 border ${
                      isDark
                        ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30 border-green-900/40'
                        : 'bg-green-50 text-green-600 hover:bg-green-100 border-green-200'
                    }`}
                    style={{ padding: '2px 4px', outline: 'none', boxShadow: 'none' }}
                  >
                    Confirm
                  </button>
                </>
              )}

              {/* The chip doubles as the drag handle for the level it belongs to. */}
              {kind !== 'entry' && showDetail && levelChip(kind as 'tp' | 'sl', shownPrice)}

              {/* Buttons for the levels this position has not placed yet, on the entry's row. */}
              {kind === 'entry' && unplacedLevels.map(level => levelChip(level, entryPrice))}

              {/* Side badge, shown for resting orders as before */}
              {kind === 'entry' && isPending && (
                <div
                  className="px-2 py-1 text-xs font-medium rounded mx-1 border"
                  style={{
                    padding: '2px 4px',
                    color: accent,
                    borderColor: `${accent}4d`,
                    background: `${accent}33`
                  }}
                >
                  {position.type}
                </div>
              )}

              {showDetail && detailBox(kind, shownPrice)}
              {showDetail && originBadge}
            </div>
          </div>

          {/* The level's line: solid for the entry, dashed for a target or a stop. It picks
              up where the controls end and runs to the price axis, so the level can be read
              against every candle between the two. */}
          <div
            className="flex-1 pointer-events-none"
            style={{
              display: showDetail ? undefined : 'none',
              height: '1px',
              transform: `scaleY(${LINE_THICKNESS})`,
              background: dashed
                ? `repeating-linear-gradient(to right, ${accent} 0px, ${accent} 3px, transparent 3px, transparent 6px)`
                : accent
            }}
          />
        </div>

        {/* Price tag, sitting over the axis where the chart prints its own prices */}
        <div
          className="absolute pointer-events-none"
          style={{
            display: showDetail ? undefined : 'none',
            right: 0,
            width: `${axisWidth}px`,
            top: 0,
            transform: 'translateY(-50%)',
            zIndex: 52
          }}
        >
          <div
            className="py-0 text-xs rounded-sm shadow"
            style={{
              padding: '1px 2px',
              // The axis is only so wide; a price that will not fit shrinks the padding
              // rather than wrapping onto a second line and pushing the tag off its level.
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textAlign: 'center',
              background: accent,
              color: '#ffffff'
            }}
          >
            {formatPrice(shownPrice)}
          </div>
        </div>
      </div>
    );
  };

  return <>{rows.map(({ kind, price, isSet }) => renderRow(kind, price, isSet))}</>;
};

export default ChartLevelOverlay;
